import { useData } from "@hooks/useData";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { components } from "@apiSchema";
import { ID } from "@models";

export interface TrendingKeyword {
    name: string;
    value: number;
}

const REQUEST_TYPE: keyof typeof apiConfig = "get category profile trending now";

const MAX_KEYWORDS = 10;

type TrendingNowKeyword = components["schemas"]["SnapTrendingNowKeywordModel"];

// Maps the API keyword model to the chart's { name, value } shape and enforces the
// display contract on the client: blank names dropped, ranked by 12-month social
// engagement growth descending, duplicate keyword names collapsed keeping the
// highest-growth instance (which also keeps BarChart's name-keyed bars unique),
// capped at the top 10. Negative growth is retained — BarChart renders it at minimum
// height rather than hiding it.
export function mapTrendingKeywords(response: TrendingNowKeyword[]): TrendingKeyword[] {
    const seenNames = new Set<string>();

    return (response ?? [])
        .map(({ wordName, twelveMonthEngagementGrowth }) => ({
            name: (wordName ?? "").trim(),
            value: twelveMonthEngagementGrowth ?? 0,
        }))
        .filter(({ name }) => !!name)
        .sort((a, b) => b.value - a.value)
        .filter(({ name }) => {
            if (seenNames.has(name)) return false;
            seenNames.add(name);
            return true;
        })
        .slice(0, MAX_KEYWORDS);
}

async function getCategoryProfileTrendingKeywords(id: ID): Promise<TrendingKeyword[]> {
    if (!id && id !== 0) return [];

    try {
        const response: TrendingNowKeyword[] = await http.get(REQUEST_TYPE, { id });
        return mapTrendingKeywords(response);
    } catch (error) {
        console.error("Fetch error:", REQUEST_TYPE, error);
        return [];
    }
}

export default function useCategoryProfileTrendingKeywords(categoryId: ID) {
    return useData<TrendingKeyword[]>([], getCategoryProfileTrendingKeywords, categoryId);
}
